import { Subject } from 'rxjs';

export enum StorageType {
  Local = 'localStorage',
  Session = 'sessionStorage',
}

export interface NoAccessWrite {
  storageType: StorageType;
  key: string;
  value: string;
}

const DEFAULT_PREFIX = 'spaces-ui';

// Storage sync message types for embed context
export interface StorageSetMessage {
  type: 'spaces:storage-set';
  key: string;
  value: string;
}

export interface StorageRemoveMessage {
  type: 'spaces:storage-remove';
  key: string;
}

export type StorageSyncMessage = StorageSetMessage | StorageRemoveMessage;

export class BrowserStorageService {
  public static hasAccessToStorage = true;

  private static localStorageDict: Record<string, string> = {};
  private static sessionStorageDict: Record<string, string> = {};
  private static _noAccessWrites$ = new Subject<NoAccessWrite>();

  // Host origin for secure postMessage in embed context (set during initialization)
  private static hostOrigin: string | null = null;

  public static noAccessWrites$ = this._noAccessWrites$.asObservable();

  /**
   * Check if we're running in an embedded context.
   */
  public static isEmbedContext(): boolean {
    return window.location.pathname.includes('/embed/');
  }

  /**
   * Set the host origin for secure postMessage communication.
   * Must be called during embed initialization before any storage operations.
   * @param origin - The verified origin of the host application (from postMessage event.origin)
   */
  public static setHostOrigin(origin: string): void {
    this.hostOrigin = origin;
  }

  /**
   * Post storage changes to host when in embed context.
   * Only posts if hostOrigin has been set (security: prevents sending to unknown origins).
   */
  private static postStorageToHost(message: StorageSyncMessage): void {
    if (!this.isEmbedContext()) {
      return;
    }

    // Security: Only post to host if we have a verified origin
    if (!this.hostOrigin) {
      console.warn('[BrowserStorageService] Cannot sync to host: hostOrigin not set');
      return;
    }

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, this.hostOrigin);
    }
  }

  /**
   * Hydrate in-memory storage from host data.
   * Called during embed initialization to restore persisted state.
   */
  public static hydrateFromHost(storage: Record<string, string>): void {
    for (const [key, value] of Object.entries(storage)) {
      this.localStorageDict[key] = value;
    }
  }

  /**
   * Get all in-memory localStorage values.
   * Useful for debugging or exporting state.
   */
  public static getLocalStorageDict(): Record<string, string> {
    return { ...this.localStorageDict };
  }

  public static getStorageKey(key: string, prefix?: string) {
    return `${prefix ?? DEFAULT_PREFIX}-${key}`;
  }

  public static getLocalStorageItem(key: string, prefix?: string) {
    const storageKey = this.getStorageKey(key, prefix);
    return this.getItem(storageKey, StorageType.Local);
  }

  public static setLocalStorageItem(key: string, value: string, prefix?: string) {
    const storageKey = this.getStorageKey(key, prefix);
    this.setItem(storageKey, value, StorageType.Local);
  }

  public static removeLocalStorageItem(key: string, prefix?: string) {
    const storageKey = this.getStorageKey(key, prefix);
    this.removeItem(storageKey, StorageType.Local);
  }

  public static getSessionStorageItem(key: string, prefix?: string) {
    const storageKey = this.getStorageKey(key, prefix);
    return this.getItem(storageKey, StorageType.Session);
  }

  public static setSessionStorageItem(key: string, value: string, prefix?: string) {
    const storageKey = this.getStorageKey(key, prefix);
    this.setItem(storageKey, value, StorageType.Session);
  }

  public static removeSessionStorageItem(key: string, prefix?: string) {
    const storageKey = this.getStorageKey(key, prefix);
    this.removeItem(storageKey, StorageType.Session);
  }

  private static getAvailableStorage(storageType: StorageType) {
    if (!this.hasAccessToStorage) {
      // no need to keep throwing errors on every storage hit
      // once we fail the first time, don't attempt to get storage again
      return undefined;
    }

    try {
      const storageTest = 'spaces-storage-test';
      const storage = storageType === StorageType.Session ? sessionStorage : localStorage;
      storage.setItem(storageTest, storageTest);
      storage.removeItem(storageTest);

      return storage;
    } catch (e) {
      console.warn(`Storage not Available: `, e);
      this.hasAccessToStorage = false;

      return undefined;
    }
  }

  private static getItem(key: string, storageType: StorageType) {
    // Try actual storage if not in embed context
    if (!this.isEmbedContext()) {
      const storage = this.getAvailableStorage(storageType);
      if (storage) {
        return storage.getItem(key);
      }
    }

    // Fallback to in-memory dict (always used in embed context, or when storage unavailable)
    if (storageType === StorageType.Session) {
      return this.sessionStorageDict[key] ?? null;
    }
    return this.localStorageDict[key] ?? null;
  }

  private static setItem(key: string, value: string, storageType: StorageType) {
    const parsedValue = this.getParsedValue(value);

    // Always store in memory dict
    if (storageType === StorageType.Session) {
      this.sessionStorageDict[key] = parsedValue;
    } else {
      this.localStorageDict[key] = parsedValue;
    }

    // In embed context, sync to host instead of using iframe's localStorage
    if (this.isEmbedContext()) {
      if (storageType === StorageType.Local) {
        this.postStorageToHost({
          type: 'spaces:storage-set',
          key,
          value: parsedValue,
        });
      }
      // Don't write to actual localStorage in embed context
      return;
    }

    // Normal context: write to actual storage if available
    const storage = this.getAvailableStorage(storageType);
    if (storage) {
      storage.setItem(key, parsedValue);
    } else {
      this._noAccessWrites$.next({
        storageType,
        key,
        value: parsedValue,
      });
    }
  }

  private static removeItem(key: string, storageType: StorageType = StorageType.Session) {
    // Always remove from memory dict
    if (storageType === StorageType.Session) {
      delete this.sessionStorageDict[key];
    } else {
      delete this.localStorageDict[key];
    }

    // In embed context, sync removal to host
    if (this.isEmbedContext()) {
      if (storageType === StorageType.Local) {
        this.postStorageToHost({
          type: 'spaces:storage-remove',
          key,
        });
      }
      // Don't touch actual localStorage in embed context
      return;
    }

    // Normal context: remove from actual storage if available
    const storage = this.getAvailableStorage(storageType);
    if (storage) {
      storage.removeItem(key);
    }
  }

  private static getParsedValue(value: string) {
    try {
      return JSON.stringify(JSON.parse(value));
    } catch (err) {
      return value;
    }
  }
}
